// Debug Logger Utility
// Centralized logging with configurable log levels

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

class Logger {
  private currentLevel: LogLevel = LogLevel.INFO
  private enabled: boolean = true

  constructor() {
    // Read log level from environment variable (VITE_LOG_LEVEL)
    const logLevel = this.parseLogLevel(import.meta.env.VITE_LOG_LEVEL)
    this.setLevel(logLevel)
    this.enabled = true
  }

  /**
   * Parse log level string from environment variable
   * @param level - Log level string (debug, info, warn, error, none)
   * @returns LogLevel enum value
   */
  private parseLogLevel(level?: string): LogLevel {
    if (!level) {
      return LogLevel.INFO // Default to INFO if not set
    }

    switch (level.toLowerCase()) {
      case 'debug':
        return LogLevel.DEBUG
      case 'info':
        return LogLevel.INFO
      case 'warn':
        return LogLevel.WARN
      case 'error':
        return LogLevel.ERROR
      case 'none':
        return LogLevel.NONE
      default:
        console.warn(`[Logger] Unknown log level "${level}", defaulting to INFO`)
        return LogLevel.INFO
    }
  }

  setLevel(level: LogLevel) {
    this.currentLevel = level
    console.log(`[Logger] Log level set to: ${LogLevel[level]}`)
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled
  }

  private log(level: LogLevel, prefix: string, message: string, ...args: any[]) {
    if (!this.enabled || level < this.currentLevel) return

    const timestamp = new Date().toISOString()
    const formattedMessage = `[${timestamp}] ${prefix} ${message}`

    switch (level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage, ...args)
        break
      case LogLevel.INFO:
        console.info(formattedMessage, ...args)
        break
      case LogLevel.WARN:
        console.warn(formattedMessage, ...args)
        break
      case LogLevel.ERROR:
        console.error(formattedMessage, ...args)
        break
    }
  }

  debug(message: string, ...args: any[]) {
    this.log(LogLevel.DEBUG, '[DEBUG]', message, ...args)
  }

  info(message: string, ...args: any[]) {
    this.log(LogLevel.INFO, '[INFO]', message, ...args)
  }

  warn(message: string, ...args: any[]) {
    this.log(LogLevel.WARN, '[WARN]', message, ...args)
  }

  error(message: string, ...args: any[]) {
    this.log(LogLevel.ERROR, '[ERROR]', message, ...args)
  }

  // Helper to log Telegram WebApp state
  logTelegramState(tg: any) {
    if (!tg) {
      this.debug('Telegram WebApp not available')
      return
    }

    this.debug('Telegram WebApp State:', {
      platform: tg.platform,
      version: tg.version,
      colorScheme: tg.colorScheme,
      isExpanded: tg.isExpanded,
      viewportHeight: tg.viewportHeight,
      viewportStableHeight: tg.viewportStableHeight,
      headerColor: tg.headerColor,
      backgroundColor: tg.backgroundColor,
      isClosingConfirmationEnabled: tg.isClosingConfirmationEnabled,
      user: tg.initDataUnsafe?.user ? {
        id: tg.initDataUnsafe.user.id,
        first_name: tg.initDataUnsafe.user.first_name,
        username: tg.initDataUnsafe.user.username,
      } : null,
    })
  }

  // Helper to log navigation events
  logNavigation(from: string, to: string) {
    this.debug(`Navigation: ${from} → ${to}`)
  }

  // Helper to log API calls
  logApiRequest(method: string, url: string, data?: any) {
    this.debug(`API ${method} ${url}`, data)
  }

  logApiResponse(method: string, url: string, status: number, data?: any) {
    if (status >= 400) {
      this.error(`API ${method} ${url} failed with status ${status}`, data)
    } else {
      this.debug(`API ${method} ${url} → ${status}`, data)
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Export for global access (useful for debugging in console)
if (typeof window !== 'undefined') {
  (window as any).logger = logger
}
